// Pure helpers for the Playo "Live TV" pages (no React, no network) so the
// Now/Next and guide-layout logic is unit-tested on its own.
//
// Data comes from the backend's public Playo endpoints (Backend/docs/PLAYO_EPG.md):
//   GET /api/client/v1/playo/channels        -> active channels of this tenant
//   GET /playo/{tenant}/{channel}/epg.json   -> { channel, programmes[{ start, stop, title, ... }] }
// All times are ISO strings with an offset; they are compared as instants.

/** Path segment the backend expects for {tenant}: the tenant id, or "global". */
export function playoTenantSegment(tenantId) {
  const id = tenantId == null ? "" : String(tenantId).trim();
  return id ? encodeURIComponent(id) : "global";
}

/** Backend origin for the non-/api Playo paths: ".../api/" -> "...". */
export function playoOrigin(apiBaseUrl) {
  return String(apiBaseUrl || "")
    .replace(/\/+$/, "")
    .replace(/\/api$/, "");
}

/** Programme guide (JSON) URL of a channel. */
export function playoEpgUrl(apiBaseUrl, tenantId, slug) {
  return `${playoOrigin(apiBaseUrl)}/playo/${playoTenantSegment(tenantId)}/${encodeURIComponent(slug)}/epg.json`;
}

const toMs = (v) => {
  const t = typeof v === "number" ? v : v instanceof Date ? v.getTime() : Date.parse(v);
  return Number.isFinite(t) ? t : null;
};

/**
 * epg.json programmes -> [{ ...p, startMs, stopMs }] sorted by start,
 * dropping rows with unreadable or empty time ranges.
 */
export function normalizeProgrammes(programmes) {
  if (!Array.isArray(programmes)) return [];
  return programmes
    .map((p) => ({ ...p, startMs: toMs(p?.start), stopMs: toMs(p?.stop) }))
    .filter((p) => p.startMs != null && p.stopMs != null && p.stopMs > p.startMs)
    .sort((a, b) => a.startMs - b.startMs);
}

/**
 * What's on now (start <= at < stop) and the next programme starting after
 * now (or after `at` during a gap). Either may be null.
 */
export function pickNowNext(programmes, at = Date.now()) {
  const t = toMs(at);
  const list = normalizeProgrammes(programmes);
  const now = list.find((p) => p.startMs <= t && t < p.stopMs) || null;
  const after = now ? now.stopMs : t;
  const next = list.find((p) => p !== now && p.startMs >= after) || null;
  return { now, next };
}

/** 0..1 progress of a programme at `at`. */
export function progressOf(programme, at = Date.now()) {
  if (!programme) return 0;
  const t = toMs(at);
  const span = programme.stopMs - programme.startMs;
  if (!(span > 0)) return 0;
  return Math.min(1, Math.max(0, (t - programme.startMs) / span));
}

/** "14:05" in the viewer's local time. */
export function formatClock(ms) {
  if (ms == null) return "";
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export const SLOT_MINUTES = 30;
const SLOT_MS = SLOT_MINUTES * 60 * 1000;

/**
 * Guide time window: starts at the half hour at/before `at`, spans `hours`.
 * Returns { startMs, endMs, slots: [ms...] } with one slot per 30 minutes.
 */
export function guideWindow(at = Date.now(), hours = 6) {
  const t = toMs(at);
  const startMs = Math.floor(t / SLOT_MS) * SLOT_MS;
  const endMs = startMs + hours * 60 * 60 * 1000;
  const slots = [];
  for (let s = startMs; s < endMs; s += SLOT_MS) slots.push(s);
  return { startMs, endMs, slots };
}

/**
 * Programmes of one channel clipped to the window, with their horizontal
 * position in px: [{ programme, left, width }]. `pxPerMinute` sets the scale.
 */
export function layoutRow(programmes, { startMs, endMs }, pxPerMinute) {
  return normalizeProgrammes(programmes)
    .filter((p) => p.stopMs > startMs && p.startMs < endMs)
    .map((p) => {
      const from = Math.max(p.startMs, startMs);
      const to = Math.min(p.stopMs, endMs);
      return {
        programme: p,
        left: ((from - startMs) / 60000) * pxPerMinute,
        width: ((to - from) / 60000) * pxPerMinute,
      };
    });
}

/**
 * Whether the viewer may watch: when the tenant requires login
 * (config.enable_login === true), a signed-in user is needed. Mirrors the
 * VOD player's rule. Playo channels carry no per-channel paywall in v1.
 */
export function canWatch(config, token) {
  if (config?.enable_login === true && !token) return false;
  return true;
}
